import {
  HStack,
  Text,
  Button,
  Alert,
  Stack,
  Separator,
  Checkbox,
  Spacer,
  ButtonGroup,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../shared/stores/RootStore";
import { useMemo } from "react";
import { t } from "i18next";
import { polygonArea } from "../../map/utils/map-utils";
import { CompositeCategory } from "../../project/models/CompositeCategory";

const MapQueryControlsBase: React.FC = () => {
  const { mapQueryStore } = useStores();

  const areaM2 = useMemo(() => {
    if (!mapQueryStore.polygon) return null;
    return polygonArea(mapQueryStore.polygon);
  }, [mapQueryStore.polygon]);

  const submit = async () => {
    mapQueryStore.importSelectedCategories();
  };

  const areaKm2 = areaM2 ? areaM2 / 1000000 : null;
  const totalSelectedFeatures = countSelectedFeatures(
    mapQueryStore.compositeCategories,
  );
  const shiftKey = "⇧";

  return (
    <Stack py={4} px={4} w={320} flexShrink={0} gap={4}>
      <Text fontWeight={"semibold"}>Start high-level assessment</Text>

      {areaKm2 == null ? (
        <Alert.Root status={"info"}>
          <Alert.Indicator />
          <Alert.Description textStyle={"sm"}>
            Press {shiftKey} and drag your mouse to select an area in the map
          </Alert.Description>
        </Alert.Root>
      ) : (
        <Text fontSize="sm">Selected area: {areaKm2.toFixed(2)} km²</Text>
      )}

      <Stack separator={<Separator />}>
        {mapQueryStore.compositeCategories.map((category) => {
          return (
            <Checkbox.Root
              disabled={category.totalFeatures === 0}
              cursor={"pointer"}
              key={category.name}
              size="sm"
              checked={category.enabled}
              onCheckedChange={({ checked }) => {
                mapQueryStore.toggleCompositeCategory(category.name);
              }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control
                bgColor={category.enabled ? category.color : undefined}
              />
              <Checkbox.Label flexGrow={1}>
                <HStack>
                  {t(`ra:compositeCategories.${category.name}`) ||
                    category.name}{" "}
                  <Spacer />
                  <span style={{ fontWeight: "normal" }}>
                    {category.totalFeatures}
                  </span>
                </HStack>
              </Checkbox.Label>
            </Checkbox.Root>
          );
        })}
      </Stack>

      <ButtonGroup
        alignSelf={"end"}
        size="sm"
        colorPalette={"primary"}
        fontSize={"sm"}
      >
        <Button
          onClick={() => mapQueryStore.resetState()}
          variant={"outline"}
          disabled={mapQueryStore.runningQuery}
        >
          Cancel
        </Button>

        <Button
          disabled={totalSelectedFeatures === 0}
          onClick={submit}
          loading={mapQueryStore.runningQuery}
        >
          Import {t(`shared:ways`, { count: totalSelectedFeatures })}
        </Button>
      </ButtonGroup>
    </Stack>
  );
};

export const MapQueryControls = observer(MapQueryControlsBase);

function countSelectedFeatures(compositeCategories: CompositeCategory[]) {
  return compositeCategories.reduce(
    (total, category) =>
      category.enabled ? total + category.totalFeatures : total,
    0,
  );
}
